import React, { useCallback, useEffect, useRef, FC } from 'react';
import styled, { useTheme } from 'styled-components';

import { DEPTH } from 'src/common/constants';
import { digits, isEmptyObj } from 'src/common/helpers';
import { useAppSelector } from 'src/hooks';

type DepthDataType = {
    sell: number[][];
    buy: number[][];
    maxAmount: number;
};

const DepthWrapper = styled.div`
    width: 100%;
    height: 100%;
    transform: translate(0, 0) translateZ(0);
    & > canvas {
        top: 0;
        left: 0;
        width: 200%;
        height: 200%;
        position: absolute;
        transform: scale(0.5, 0.5) scaleZ(1);
        pointer-events: none;
        transform-origin: 0 0;
    }
`;

interface DepthProps {
    activeChart?: string;
}

const DepthChart: FC<DepthProps> = ({ activeChart }) => {
    const { colors } = useTheme();
    const { themeMode } = useAppSelector((state) => state.global);

    const themeRef = useRef({
        gap: 6,
        color: [
            '',
            themeMode === 'dark' ? '#999999' : '#A6A6A6',
            themeMode === 'dark' ? '#475563' : '#B0BFDB',
        ],
        bgColor: colors.background.primary,
        sell: ['rgba(246,92,92,0.4)', 'rgba(246,92,92,0.1)', colors.exchange.sell],
        buy: ['rgba(62,183,114,0.4)', 'rgba(62,183,114,0.1)', colors.exchange.buy],
        space: [140, 50],
        limit: 100,
        descStyle: {
            w: 240,
            h: 140,
            r: [20, 12],
            text: '22px IBMPlexSans-Regular',
            textColor: themeMode === 'dark' ? '#FFFFFF' : '#202F44',
            bgColor: themeMode === 'dark' ? colors.background.primary : 'rgba(229,237,254,0.8)',
            pointColor: [
                'rgba(246,92,92,0.3)',
                colors.exchange.sell,
                'rgba(62,183,114,0.3)',
                colors.exchange.buy,
            ],
        },
    });
    const canvasDataRef = useRef<{
        width: number;
        height: number;
        canvasPageX?: number;
        canvasPageY?: number;
        ctx?: CanvasRenderingContext2D | null;
    }>({
        width: 0,
        height: 0,
        canvasPageX: 0,
        canvasPageY: 0,
        ctx: null,
    });
    const mousePositionRef = useRef([] as number[]);
    const bgRef = useRef<HTMLCanvasElement>(null);
    const desRef = useRef<HTMLCanvasElement>(null);
    const maximumAmount = useRef(0);

    const { mergedDepth } = useAppSelector((state) => state.socket);
    const { currentSymbol } = useAppSelector((state) => state.spot);

    useEffect(() => {
        const maxDigits = DEPTH[currentSymbol?.minPricePrecision];
        const basePrecision = DEPTH[currentSymbol?.basePrecision];
        const theme = themeRef.current;
        let { width, height } = canvasDataRef.current;
        let { gap, color, space } = theme;
        const depthInfo = setDepthData();
        if (!depthInfo) return;
        let { sell, buy, maxAmount } = depthInfo;
        maximumAmount.current = maxAmount;
        width -= space[0];
        height -= space[1];
        const context = bgRef.current?.getContext('2d');
        if (!context) {
            return;
        }
        context?.clearRect(0, 0, 30000, 30000);

        const minBuyPrice = buy.length ? Number(Number(buy[buy.length - 1][0])) : 0;
        const maxBuyPrice = buy.length ? Number(Number(buy[0][0])) : 0;
        const fixBuy = maxBuyPrice - minBuyPrice;
        const minSellPrice = sell.length ? Number(Number(sell[0][0])) : 0;
        const maxSellPrice = sell.length ? Number(Number(sell[sell.length - 1][0])) : 0;
        const fixSell = maxSellPrice - minSellPrice;
        let x = 0,
            y = 0;
        if (buy.length) {
            context.beginPath();
            context.strokeStyle = theme.buy[2];
            for (let i = 0; i < buy.length; i++) {
                x =
                    (width / 2 - gap) *
                    (fixBuy ? 1 - (maxBuyPrice - Number(buy[i][0])) / fixBuy : 0);
                y = height - (Number(buy[i][2]) / maxAmount) * height;
                if (i === 0) {
                    context.moveTo(width / 2 - gap + 0.5, height);
                    x = width / 2 - gap + 0.5;
                }
                context.lineTo(x, y);
            }

            context.lineTo(0, y);
            context.lineTo(0, height);
            context.lineTo(width / 2 - gap + 0.5, height);

            const lineGard = context.createLinearGradient(0, 0, 0, height);
            lineGard.addColorStop(0, theme.buy[0]);
            lineGard.addColorStop(1, theme.buy[1]);
            context.fillStyle = lineGard;

            context.fill();
            context.closePath();
        }

        x = 0;
        y = 0;
        if (buy.length) {
            context.beginPath();
            context.strokeStyle = theme.buy[2];
            context.lineWidth = 4;
            context.lineCap = 'round';
            for (let i = 0; i < buy.length; i++) {
                x =
                    (width / 2 - gap) *
                    (fixBuy ? 1 - (maxBuyPrice - Number(buy[i][0])) / fixBuy : 0);
                y = height - (Number(buy[i][2]) / maxAmount) * height;
                if (i === 0) {
                    context.moveTo(width / 2 - gap + 0.5, height);
                    x = width / 2 - gap + 0.5;
                }
                context.lineTo(x, y);
            }
            context.lineTo(0, y);
            context.stroke();
            context.closePath();
        }

        if (sell.length) {
            context.beginPath();
            for (let i = 0; i < sell.length; i++) {
                //x = Math.min(width / 2 + i * scaleW + gap, width);
                x =
                    width / 2 +
                    (width / 2) * (fixSell ? 1 - (maxSellPrice - sell[i][0]) / fixSell : 0) +
                    gap;
                y = height - (sell[i][2] / maxAmount) * height;
                if (i === 0) {
                    context.moveTo(width / 2 + gap - 0.5, height);
                    x = width / 2 + gap - 0.5;
                }
                context.lineTo(x, y);
            }

            context.lineTo(width + gap, y);
            context.lineTo(width + gap, height);
            context.lineTo(width / 2 + gap - 0.5, height);

            const lineGard = context.createLinearGradient(0, 0, 0, height);
            lineGard.addColorStop(0, theme.sell[0]);
            //lineGard.addColorStop(0.5, this.theme.sell[0]);
            lineGard.addColorStop(1, theme.sell[1]);
            context.fillStyle = lineGard;

            context.fill();
            context.closePath();
        }

        if (sell.length) {
            context.beginPath();
            context.strokeStyle = theme.sell[2];
            //context.fillStyle = color[1];
            for (let i = 0; i < sell.length; i++) {
                //x = Math.min(width / 2 + i * scaleW + gap, width);
                x =
                    width / 2 +
                    (width / 2) * (fixSell ? 1 - (maxSellPrice - sell[i][0]) / fixSell : 0) +
                    gap;
                y = height - (sell[i][2] / maxAmount) * height;
                if (i === 0) {
                    context.moveTo(width / 2 + gap - 0.5, height);
                    x = width / 2 + gap - 0.5;
                }
                context.lineTo(x, y);
            }

            context.lineTo(width + gap, y);
            context.stroke();
        }

        context.lineWidth = 1;
        context.beginPath();
        context.strokeStyle = color[2];
        context.moveTo(0, height + 1.5);
        context.lineTo(width + gap + 10, height + 1.5);
        context.stroke();
        context.closePath();

        context.fillStyle = color[2];
        context.font = theme.descStyle.text;
        // context.canvasLetterSpacing = 0;

        for (let i = 0; i < 4; i++) {
            x = width / 2 - (i * 0.25 * width) / 2 - gap - 32;
            y = height + 32;
            context.fillStyle = color[1];
            context.font = theme.descStyle.text;
            // context.canvasLetterSpacing = 0;
            context.fillText((maxBuyPrice - fixBuy * i * 0.25).toFixed(maxDigits), x, y);
            context.beginPath();
            context.strokeStyle = color[2];
            context.moveTo(x + 32.5, y - 32);
            context.lineTo(x + 32.5, y - 24);
            context.stroke();
            context.closePath();
        }

        for (let i = 1; i <= 3; i++) {
            //x = width / 2 + i * scaleW - 32;
            x = width / 2 + (i * 0.3 * width) / 2 - 32;
            y = height + 32;
            context.fillStyle = color[1];
            context.font = theme.descStyle.text;
            // context.canvasLetterSpacing = 0;
            context.fillText((minSellPrice + fixSell * i * 0.3).toFixed(maxDigits), x, y);

            context.beginPath();
            context.strokeStyle = color[2];
            context.moveTo(x + 32.5, y - 32);
            context.lineTo(x + 32.5, y - 24);
            context.stroke();
            context.closePath();
        }

        context.lineWidth = 1;
        context.beginPath();
        context.strokeStyle = color[2];
        context.moveTo(width + gap + 0.5, height);
        context.lineTo(width + gap + 0.5, 0);
        context.stroke();
        context.closePath();

        let seg = maxAmount / 7;
        for (let i = 0; i <= 7; i++) {
            x = width + 28;
            y = height - ((seg * i) / maxAmount) * height + 12;
            if (i == 7) {
                y = 20;
            }
            context.fillStyle = color[1];
            context.font = theme.descStyle.text;
            // context.canvasLetterSpacing = 0;
            context.fillText(
                seg * i > 1000
                    ? `${((seg * i) / 1000).toFixed(2)}K`
                    : `${(seg * Number(i)).toFixed(basePrecision)}`,

                x - 8,
                y,
            );
            if (i > 0) {
                context.beginPath();
                context.strokeStyle = color[2];
                context.moveTo(x - 20.5, y - 8);
                context.lineTo(x - 11.5, y - 8);
                context.stroke();
                context.closePath();
            }
        }
        if (mousePositionRef.current.length) {
            mousemove();
        }
    }, [mergedDepth, currentSymbol, themeMode, canvasDataRef.current]);

    const mousemove = (e: any = undefined) => {
        let { width, height, canvasPageX, canvasPageY, ctx } = canvasDataRef.current;
        let { gap, space, descStyle } = themeRef.current;
        if (!ctx || canvasPageX === undefined || canvasPageY === undefined) return;
        const sell: string[][] = mergedDepth.a;
        const buy: string[][] = mergedDepth.b;
        const maxAmount = maximumAmount.current;
        width -= space[0];
        height -= space[1];
        const left = e ? e.pageX - canvasPageX : mousePositionRef.current[0];
        const top = e ? e.pageY - canvasPageY : mousePositionRef.current[1];
        mousePositionRef.current = [left, top];
        const maxDigits = DEPTH[currentSymbol?.minPricePrecision];
        const basePrecision = DEPTH[currentSymbol?.basePrecision];
        //const area = [0, 0, width, height];
        if (
            left < 0 ||
            left * 2 > width ||
            top < 0 ||
            top * 2 > height ||
            (left * 2 > width / 2 - gap && left * 2 < width / 2)
        ) {
            clear();
            return;
        }
        const size = getPoint(left, top, width, height, ctx);
        // if (size > width / 2 - gap + 0.5 && size < width / 2 + gap - 0.5) {
        //     return;
        // }
        if (!size) return;

        let price = 0;
        let type = '';
        if (size[0] <= width / 2 - gap + 0.5) {
            type = 'buy';
            const scale = (size[0] / (width / 2 - gap)).toFixed(6);
            const fix =
                buy[0] && buy[buy.length - 1]
                    ? (Number(buy[0][0]) - Number(buy[buy.length - 1][0])).toFixed()
                    : 0;

            if (buy.length) {
                if (size[0] == width / 2 - gap + 0.5) {
                    price = Number(buy[0][0]);
                } else {
                    price = Number(scale) * Number(fix) + Number(buy[buy.length - 1][0]);
                }
            } else {
                price = 0;
            }
        }
        if (size[0] >= width / 2 + gap - 0.5) {
            type = 'sell';
            const scale = (size[0] - gap - width / 2) / Number((width / 2).toFixed(6));

            const fix =
                sell[0] && sell[sell.length - 1]
                    ? (Number(sell[sell.length - 1][0]) - Number(sell[0][0])).toFixed(2)
                    : 0;
            if (sell.length) {
                if (size[0] === width / 2 + gap - 0.5) {
                    price = Number(sell[sell.length - 1][0]);
                } else {
                    price = Number((scale * Number(fix) + Number(sell[0][0])).toFixed(2));
                }
            } else {
                price = 0;
            }
        }
        price = digits(price, maxDigits);

        let amount = Number((((height - size[1]) / height) * maxAmount).toFixed(2));

        amount = digits(amount, basePrecision);
        let descSize = {
            x: 20 + size[0],
            y: 20 + size[1],
            w: descStyle.w,
            h: descStyle.h,
        };
        if (descSize.x + descSize.w > width) {
            descSize.x = descSize.x - descSize.w - descStyle.r[0] * 2;
        }
        if (descSize.y + descSize.h > height) {
            descSize.y = descSize.y - descSize.h - descStyle.r[0] * 2;
        }
        drawLayer(
            size[0],
            size[1],
            descSize.x,
            descSize.y,
            descSize.w,
            descSize.h,
            price,
            amount,
            type,
        );
    };

    const drawLayer = useCallback(
        (
            x: number,
            y: number,
            x1: number,
            y1: number,
            width: number,
            height: number,
            price: number,
            amount: number,
            type: string,
        ) => {
            const ctx: CanvasRenderingContext2D = desRef.current?.getContext('2d')!;
            const theme = themeRef.current;
            const style = theme.descStyle;
            ctx.clearRect(0, 0, 30000, 30000);

            ctx.beginPath();
            ctx.fillStyle = type === 'buy' ? style.pointColor[2] : style.pointColor[0];
            ctx.arc(x, y, style.r[0], 0, Math.PI * 2);
            ctx.fill();
            ctx.closePath();

            ctx.beginPath();
            ctx.fillStyle = type === 'buy' ? style.pointColor[3] : style.pointColor[1];
            ctx.arc(x, y, style.r[1], 0, Math.PI * 2);
            ctx.fill();
            ctx.closePath();

            let min_size = Math.min(x1 + width, y1 + height);
            let r = 10;
            if (r > min_size / 2) r = min_size / 2;
            ctx.beginPath();
            ctx.fillStyle = style.bgColor;
            ctx.moveTo(x1 + r, y1);

            ctx.arcTo(x1 + width, y1, x1 + width, y1 + height, r);
            ctx.arcTo(x1 + width, y1 + height, x1, y1 + height, r);
            ctx.arcTo(x1, y1 + height, x1, y1, r);
            ctx.arcTo(x1, y1, x1 + width, y1, r);
            ctx.closePath();
            ctx.fill();

            ctx.font = style.text;
            ctx.fillStyle = style.textColor;
            ctx.fillText(`Price  ${price}`, x1 + 30, y1 + 48);
            ctx.fillText(`Total  ${amount}`, x1 + 30, y1 + 98);
        },
        [],
    );

    const getPoint = useCallback(
        (
            x: number,
            y: number,
            w: number,
            h: number,
            ctx: CanvasRenderingContext2D | null,
        ): [number, number] | undefined => {
            if (!ctx) return undefined;

            const search = (x1: number, y1: number, y2: number): [number, number] | undefined => {
                if (y1 - y2 <= 2) {
                    return [x1, y1 - 1];
                }
                let data = ctx.getImageData(x1, y1, 1, 1).data;
                let data2 = ctx.getImageData(x1, y2, 1, 1).data;
                if (data2[3] != 0 && data[3] != 0) {
                    return search(x1, y2, 0);
                }
                if (data2[3] == 0 && data[3] == 0) {
                    return search(x1, h - 1, y1);
                }
                if (data2[3] == 0 && data[3] != 0) {
                    let ny = Number(((y1 - y2) / 2 + y2).toFixed(0));
                    let d = ctx.getImageData(x1, ny, 1, 1).data;
                    if (d[3] != 0) {
                        return search(x1, ny, y2);
                    } else {
                        return search(x1, y1, ny);
                    }
                }
            };

            return search(x * 2, Math.floor(h / 2), 0);
        },
        [],
    );

    const clear = useCallback(() => {
        const context = desRef.current?.getContext('2d')!;
        context?.clearRect(0, 0, 30000, 30000);
    }, []);

    const reset = useCallback(() => {
        const mainContainer = document.getElementById('chartMainContainer');
        if (bgRef.current === null || desRef.current === null || mainContainer === null) return;
        const canvasBg = bgRef.current;
        const canvasDes = desRef.current;
        const width = (mainContainer.clientWidth || 0) * 2;
        const height = (mainContainer.clientHeight || 0) * 2;

        canvasBg.setAttribute('width', width.toString());
        canvasBg.setAttribute('height', height.toString());
        canvasDes.setAttribute('width', width.toString());
        canvasDes.setAttribute('height', height.toString());

        const sizes = mainContainer.getBoundingClientRect();
        const data: {
            width: number;
            height: number;
            canvasPageX: number;
            canvasPageY: number;
            ctx: CanvasRenderingContext2D;
        } = {
            width,
            height,
            canvasPageX: sizes.left + window.pageXOffset,
            canvasPageY: sizes.top + window.pageYOffset,
            ctx: canvasBg.getContext('2d')!,
        };
        canvasDataRef.current = data;
    }, []);

    const pageResize = useCallback(() => {
        if (bgRef.current === null) return;
        reset();
    }, []);

    const setDepthData = (): DepthDataType | undefined => {
        if (!mergedDepth || isEmptyObj(mergedDepth)) return;

        const data = mergedDepth;
        const buy = data.b ? data.b.slice(0, 100) : [];
        const sell = data.a ? data.a.slice(0, 100) : [];
        let maxAmount = 0;
        let t = 0;
        let b: number[][] = [];
        let s: number[][] = [];

        for (let i = 0; i < buy.length; i++) {
            if (Math.abs((Number(buy[i][0]) - Number(buy[0][0])) / Number(buy[0][0])) <= 0.6) {
                b.push([Number(buy[i][0]), Number(buy[i][1]), 0]);
                b[b.length - 1][2] =
                    Number(buy[i][1]) + Number(b.length > 1 ? b[b.length - 2][2] : 0);
                t = Number(b[b.length - 1][2]);
                if (t > maxAmount) {
                    maxAmount = t;
                }
            }
        }

        for (let i = 0; i < sell.length; i++) {
            if (Math.abs((Number(sell[i][0]) - Number(sell[0][0])) / Number(sell[0][0])) <= 0.6) {
                s.push([Number(sell[i][0]), Number(sell[i][1]), 0]);
                s[s.length - 1][2] =
                    Number(sell[i][1]) + Number(s.length > 1 ? s[s.length - 2][2] : 0);
                t = Number(s[s.length - 1][2]);
                if (t > maxAmount) {
                    maxAmount = t;
                }
            }
        }

        maxAmount *= 1.05;

        return {
            sell: s,
            buy: b,
            maxAmount: maxAmount,
        };
    };

    useEffect(() => {
        themeRef.current = {
            ...themeRef.current,
            color: [
                '',
                themeMode === 'dark' ? '#999999' : '#A6A6A6',
                themeMode === 'dark' ? '#475563' : '#B0BFDB',
            ],
            descStyle: {
                ...themeRef.current.descStyle,
                textColor: themeMode === 'dark' ? '#FFFFFF' : '#202F44',
                bgColor: themeMode === 'dark' ? colors.background.primary : 'rgba(229,237,254,0.8)',
            },
        };
        reset();
    }, [themeMode]);

    useEffect(() => {
        const chartMainContainer = document.getElementById('chartMainContainer');
        if (!chartMainContainer) return;

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (
                    canvasDataRef.current.height !== entry.target.clientHeight * 2 ||
                    canvasDataRef.current.width !== entry.target.clientWidth * 2
                ) {
                    reset();
                }
            }
        });

        observer.observe(chartMainContainer);

        return () => {
            observer.unobserve(chartMainContainer);
        };
    }, []);

    useEffect(() => {
        return () => {
            clear();
            reset();
        };
    }, [clear, reset]);

    useEffect(() => {
        window.addEventListener('resize', pageResize);
        return () => {
            window.removeEventListener('resize', pageResize);
        };
    }, [pageResize]);

    useEffect(() => {
        if (mousePositionRef.current.length === 0) {
            clear();
        }
    }, [mousePositionRef.current]);

    return (
        <DepthWrapper
            onMouseMove={mousemove}
            onMouseOut={() => (mousePositionRef.current = [])}
            style={activeChart === 'chart' ? { display: 'none' } : {}}
        >
            <canvas ref={bgRef} />
            <canvas ref={desRef} />
        </DepthWrapper>
    );
};

export default DepthChart;
